import { HttpClient } from '@angular/common/http';
import { ChangeDetectionStrategy, Component, inject, signal, ViewEncapsulation } from '@angular/core';
import { JwtHelperService } from '@auth0/angular-jwt';
import { AppSecurityRight, TenantInfoRequest, UserType } from '@jms/model';
import { ConfigService } from '@jms/ui/ui.standalone.services/config.service';
import { AuthTokenService, PORTAL_AUTH_TOKEN_KEY } from '@jms/utils/util.authentication';
import { UtilService } from '@jms/utils/util.services';
import { ButtonComponent } from '@workbuddy/ui/components/button';

type PortalOption = {
    _id: 'clients' | 'contractors';
    title: string;
    description: string;
    icon: string;
    path: string;
}

@Component({
    selector: 'portal-select',
    templateUrl: './portal.select.component.html',
    styleUrls: ['./portal.select.component.less'],
    standalone: true,
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        ButtonComponent,
    ],
})
export class PortalSelectComponent {

    private readonly jwt = inject(JwtHelperService);
    private readonly authTokenService = inject(AuthTokenService);
    private readonly http = inject(HttpClient);

    protected readonly options = this.getOptions();
    protected readonly $businessName = signal<string>('');
    protected readonly $businessLogo = signal<string>('');

    constructor() {

        if (!this.options.length) {
            this.logout();
            return;
        }

        this.http
            .get<TenantInfoRequest>('/api/info')
            .subscribe(info => {
                this.$businessName.set(info?.business?.name ?? '');
                this.$businessLogo.set(info?.business?.logo ?? '');
            });
    }

    protected open(path: string): void {

        // Path-mode portals share this origin's localStorage but read their session from
        // portal_auth_token, so seed it before handing over — only when the token actually
        // carries the login right for the chosen portal.
        const token = this.authTokenService.getToken();
        const rights = this.getCurrentPortalRole(token)?.rights ?? [];
        const requiredRight = path.startsWith('/contractors') ? AppSecurityRight.LoginContractor : AppSecurityRight.LoginClient;

        if (token && rights.includes(requiredRight)) {
            localStorage.setItem(PORTAL_AUTH_TOKEN_KEY, token);
        }

        window.location.assign(path);
    }

    protected logout(): void {

        this.authTokenService.clear();
        UtilService.navigate(ConfigService.environment.jms.logoutHref, window.location.origin);
    }

    private getOptions(): PortalOption[] {

        const token = this.authTokenService.getToken();
        const role = this.getCurrentPortalRole(token);
        const rights = role?.rights ?? [];
        const options: PortalOption[] = [];

        if (rights.includes(AppSecurityRight.LoginClient)) {
            options.push(this.getOption('clients', 'Continue as a client', 'Use the client portal to view your jobs, sites, quotes, and invoices.', 'fa-regular fa-building-user', '/clients/'));
        }

        if (rights.includes(AppSecurityRight.LoginContractor)) {
            options.push(this.getOption('contractors', 'Continue as a contractor', 'Use the contractor portal to view your work, allocations, and compliance.', 'fa-regular fa-helmet-safety', '/contractors/'));
        }

        return options;
    }

    private getOption(_id: PortalOption['_id'], title: string, description: string, icon: string, path: string): PortalOption {

        return {
            _id,
            title,
            description,
            icon,
            path,
        };
    }

    private getCurrentPortalRole(token: string | null): { type?: UserType; rights?: number[] } | null {

        if (!token) return null;

        const user = this.jwt.decodeToken(token) as { role?: { type?: UserType; rights?: number[] }; tenants?: Array<{ role?: { type?: UserType; rights?: number[] }; tenantUrl?: string; url?: string; tenant?: { url?: string } }> };
        const currentHost = window.location.hostname.toLowerCase();
        const tenants = user?.tenants ?? [];
        const tenant = tenants.find(x => this.getTenantUrl(x) === currentHost) ?? (tenants.length === 1 ? tenants[0] : null);
        const role = tenant?.role ?? user?.role;

        return role?.type === UserType.LoginContact
            ? role
            : null;
    }

    private getTenantUrl(tenant: { tenantUrl?: string; url?: string; tenant?: { url?: string } }): string {

        return (tenant?.tenantUrl ?? tenant?.url ?? tenant?.tenant?.url ?? '').toLowerCase();
    }
}
